import { useHistory } from "react-router";
import {
    AddNewIcon,
    AIFActiveIcon,
    AIFIcon,
    BondsActiveIcon,
    BondsIcon,
    DashboardInactiveIcon,
    DirectEquityActiveIcon,
    DirectEquityIcon,
    DownloadIcon,
    MutualFundsIcon,
    PaymentsActiveIcon,
    PaymentsInactiveIcon,
    PMSActiveIcon,
    PMSIcon,
    SidebarDashboardIcon,
    ViewEmailConsentIcon,
    ViewExistingIcon
} from "./customSVGs";
import {
    Dispatch,
    SetStateAction,
    useMemo
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getParams } from "../redux-store/actions/application";
import { initialState } from "../redux-store/reducers/queryParams";
import {
    ActiveAddNewIcon,
    ActiveDownloadIcon,
    ActiveViewEmailConsentIcon,
    ActiveViewExistingIcon,
    InvestorActiveReportsIcon,
    InvestorMFActiveIcon,
    InvestorReportsIcon,
    ActiveRecommendationsIcon,
    InactiveRecommendationsIcon
} from "../assets/SvgIcons";
import { RootStateType } from "../redux-store/reducers";
import {
    ENABLE_ORDER_MANAGEMENT,
    ENABLE_PAYMENT_CHECKER,
    ROLE_TYPE,
    USER_ROLES
} from "../utils/constant";
import {
    setAppliedRecommendationsFilters,
    setRecommendationsActiveCategories
} from "../redux-store/actions/investorProfile";
import { isModuleAllowed } from "../utils/utilityFunctions";

interface MenuItem {
    name: string;
    activeIcon?: JSX.Element;
    inActiveIcon?: JSX.Element;
    handleClick: () => void;
    route?: Array<string>;
    dropdownId?: string;
    isDisabled: boolean;
}

interface SidebarItem {
    isSubMenuItem: boolean;
    menuItem: MenuItem;
    subMenuItem: MenuItem[];
}

export default function getSideBarData({
    activeCategory,
    setActiveCategory
}: {
    activeCategory: string;
    setActiveCategory: Dispatch<SetStateAction<string>>;
}): Array<SidebarItem> {
    const history = useHistory();
    const dispatch = useDispatch();

    const {
        role = '',
        subRole = "",
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);

    const { investorClientId = "" } = useSelector((store: RootStateType) => store.id)

    const sideBarData = useMemo(() => {
        return (
            [
                //   NON INVESTOR DASHBOARD
                ...((
                    ![USER_ROLES.INVESTOR].includes(role) &&
                    isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" })
                ) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Dashboard',
                        activeIcon: <SidebarDashboardIcon />,
                        inActiveIcon: <DashboardInactiveIcon />,
                        handleClick: () => history.push('/dashboard'),
                        route: ['/dashboard'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR DASHBOARD
                ...(([USER_ROLES.INVESTOR].includes(role) && isModuleAllowed({ value: "INVDashboard", compareArr: permissions, objKey: "permissonCode" })) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Dashboard',
                        activeIcon: <SidebarDashboardIcon />,
                        inActiveIcon: <DashboardInactiveIcon />,
                        handleClick: () => history.push('/investor-dashboard'),
                        route: ['/investor-dashboard'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR RECOMMENDATIONS
                ...([USER_ROLES.INVESTOR].includes(role) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Recommendations',
                        activeIcon: <ActiveRecommendationsIcon />,
                        inActiveIcon: <InactiveRecommendationsIcon />,
                        handleClick: () => {
                            dispatch(setRecommendationsActiveCategories({
                                primaryCategory: "New Purchase",
                                secondaryCategory: "Lumpsum"
                            }))
                            dispatch(setAppliedRecommendationsFilters([]))

                            history.push('/investor-recommendations')
                        },
                        route: ['/investor-recommendations'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // INVESTOR PRODUCTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'INVEST',
                        handleClick: () => setActiveCategory(activeCategory === 'invest' ? '' : 'invest'),
                        dropdownId: 'invest',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // INVESTOR MUTUAL FUNDS
                        ...([USER_ROLES.INVESTOR].includes(role) ? [{
                            name: 'Mutual Funds',
                            activeIcon: <InvestorMFActiveIcon />,
                            inActiveIcon: <MutualFundsIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/mutual-fund?tab=Overview', {
                                    clientCode: investorClientId ?? ""
                                })
                            },
                            route: ['/investor-dashboard/mutual-fund'],
                            isDisabled: false
                        }] : []),

                        // INVESTOR DIRECT EQUITY
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DirectEQDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Direct Equity',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/direct-equity')
                            },
                            route: ["/investor-dashboard/direct-equity"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR PMS
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "PMSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'PMS',
                            activeIcon: <PMSActiveIcon />,
                            inActiveIcon: <PMSIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/pms')
                            },
                            route: ["/investor-dashboard/pms"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR DEBT
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DebtDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Debt',
                            activeIcon: <BondsActiveIcon />,
                            inActiveIcon: <BondsIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/debt')
                            },
                            route: ["/investor-dashboard/debt"],
                            isDisabled: false
                        }] : []),

                        // INVESTOR AIF
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "AIFDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Alternates',
                            activeIcon: <AIFActiveIcon />,
                            inActiveIcon: <AIFIcon />,
                            handleClick: () => {
                                history.push('/investor-dashboard/alternates')
                            },
                            route: ["/investor-dashboard/alternates"],
                            isDisabled: false
                        }] : [])
                    ],
                },

                //   NON INVESTOR PRODUCTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'PRODUCTS',
                        handleClick: () => setActiveCategory(activeCategory === 'products' ? '' : 'products'),
                        dropdownId: 'products',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // NON INVESTOR MUTUAL FUNDS
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            (
                                isModuleAllowed({ value: 'MFOrders', compareArr: permissions, objKey: "permissonCode" }) ||
                                isModuleAllowed({ value: 'OrderRecommend', compareArr: permissions, objKey: "permissonCode" })
                            )
                        ) ? [{
                            name: 'Mutual Funds',
                            activeIcon: <InvestorMFActiveIcon />,
                            inActiveIcon: <MutualFundsIcon />,
                            handleClick: () => {
                                history.push('/product/mutual-fund')
                            },
                            route: ['/product/mutual-fund'],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR DIRECT EQUITY
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DirectEQDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Direct Equity',
                            activeIcon: <DirectEquityActiveIcon />,
                            inActiveIcon: <DirectEquityIcon />,
                            handleClick: () => {
                                history.push('/product/direct-equity')
                            },
                            route: ["/product/direct-equity"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR PMS
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "PMSDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'PMS',
                            activeIcon: <PMSActiveIcon />,
                            inActiveIcon: <PMSIcon />,
                            handleClick: () => {
                                history.push('/product/pms')
                            },
                            route: ["/product/pms"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR DEBT
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "DebtDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Debt',
                            activeIcon: <BondsActiveIcon />,
                            inActiveIcon: <BondsIcon />,
                            handleClick: () => {
                                history.push('/product/debt')
                            },
                            route: ["/product/debt"],
                            isDisabled: false
                        }] : []),

                        // NON INVESTOR AIF
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "AIFDashboard", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Alternates',
                            activeIcon: <AIFActiveIcon />,
                            inActiveIcon: <AIFIcon />,
                            handleClick: () => {
                                history.push('/product/alternates')
                            },
                            route: ["/product/alternates"],
                            isDisabled: false
                        }] : [])
                    ],
                },

                //   ONBOARDING APPLICANTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'APPLICATIONS',
                        handleClick: () => setActiveCategory(activeCategory === 'applications' ? '' : 'applications'),
                        dropdownId: 'applications',
                        isDisabled: false
                    },
                    subMenuItem: [

                        // ONBOARD NEW APPLICANT
                        ...(isModuleAllowed({ value: "INVOnboard", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'Add New',
                                activeIcon: <ActiveAddNewIcon />,
                                inActiveIcon: <AddNewIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/onboarding')
                                },
                                route: ['/onboarding/enter-pan-number'],
                                isDisabled: false
                            }] : []),

                        // KYC APPLICATIONS 
                        ...(isModuleAllowed({ value: "INVOnboard", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'KYC Applications',
                                activeIcon: <ActiveViewExistingIcon />,
                                inActiveIcon: <ViewExistingIcon />,
                                handleClick: () => {
                                    history.push('/kyc-applications')
                                },
                                route: ['/kyc-applications'],
                                isDisabled: false
                            }] : []),

                        // VIEW EXISTING APPLICANT
                        ...(isModuleAllowed({ value: "APPL", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'View Existing',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                if ([USER_ROLES.AMCAPPROVER].includes(role)) {
                                    history.push('/applications-list')
                                    return
                                } else {
                                    dispatch(getParams(initialState))
                                    history.push('/applications')
                                }
                            },
                            route: [[USER_ROLES.AMCAPPROVER].includes(role) ? "/applications-list" : "/applications"],
                            isDisabled: [USER_ROLES.POACHECKER].includes(subRole)
                        }] : []),

                        // VIEW EMAIL CONSENT    
                        ...(isModuleAllowed({ value: "INVOnboard", compareArr: permissions, objKey: "permissonCode" }) ?
                            [{
                                name: 'View Email Consent',
                                activeIcon: <ActiveViewEmailConsentIcon />,
                                inActiveIcon: <ViewEmailConsentIcon />,
                                handleClick: () => {
                                    dispatch(getParams(initialState))
                                    history.push('/view-consent-email-list')
                                },
                                route: ["/view-consent-email-list"],
                                isDisabled: false
                            }] : []),

                        // COMPLIANCE APPROVAL
                        ...(isModuleAllowed({ value: "complianceChecker", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Compliance Approval',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                history.push('/compliance-approval')
                            },
                            route: [
                                "/compliance-approval",
                                "/compliance-application-details/:id"
                            ],
                            isDisabled: false
                        }] : [])

                        // {
                        //     name: 'Onboarding DashBoard',
                        //     activeIcon: <ActiveInvestorOnBoardingIcon />,
                        //     inActiveIcon: <InvestorOnBoardingIcon />,
                        //     handleClick: () => {
                        //         dispatch(getParams(initialState))
                        //         history.push('/onboard-overview')
                        //     },
                        //     route: ["/onboard-overview"],
                        //     isDisabled: false
                        // }
                    ],
                },

                // REPORTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'REPORTS',
                        handleClick: () => setActiveCategory(activeCategory === 'reports' ? '' : 'reports'),
                        dropdownId: 'reports',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // NON INVESTOR REPORTS
                        ...((
                            ![USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INVReports", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Transaction Reports',
                            activeIcon: <ActiveDownloadIcon />,
                            inActiveIcon: <DownloadIcon />,
                            handleClick: () => {
                                dispatch(getParams(initialState))
                                history.push('/reports-download/mf-client-statement')
                            },
                            route: [
                                '/reports-download/mf-client-statement',
                                '/reports-download/mf-transaction-wise-holding-report',
                                '/reports-download/holding-report',
                                '/reports-download/capital-gain-statement',
                                '/reports-download/transaction-history-report',
                                // '/reports-download/ria-report',
                                '/reports-download/corporate-action-report'
                            ],
                            isDisabled: false
                        }] : []),

                        // ONBOARDING REPORTS
                        ...(
                            // (
                            //     [USER_ROLES.AMC_ADMIN, USER_ROLES.AMCAPPROVER].includes(role) ||
                            //     (
                            //         [USER_ROLES.RM].includes(role) &&
                            //         [ROLE_TYPE.RWM, ROLE_TYPE.RTL].includes(ROLE_TYPE[`${subRole ?? ""}` as keyof typeof ROLE_TYPE])
                            //     )
                            // )
                            (isModuleAllowed({ value: "OnboardReport", compareArr: permissions, objKey: "permissonCode" }))
                                ? [{
                                    name: 'Onboarding Reports',
                                    activeIcon: <ActiveDownloadIcon />,
                                    inActiveIcon: <DownloadIcon />,
                                    handleClick: () => {
                                        dispatch(getParams(initialState))
                                        history.push('/reports')
                                    },
                                    route: ["/reports"],
                                    isDisabled: false
                                }] : [])
                    ],
                },

                //   ANALYTICS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'ANALYTICS',
                        handleClick: () => setActiveCategory(activeCategory === 'analytics' ? '' : 'analytics'),
                        dropdownId: 'analytics',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // MUTUAL FUND RESEARCH
                        ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Mutual Fund Research',
                            activeIcon: <></>,
                            inActiveIcon: <></>,
                            handleClick: () => {
                                dispatch(getParams(initialState))
                                history.push('/analytics/mutual-fund-research')
                            },
                            route: ['/analytics/mutual-fund-research'],
                            isDisabled: true
                        }] : []),

                        // PORTFOLIO ANALYTICS
                        ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Portfolio Analytics',
                            activeIcon: <></>,
                            inActiveIcon: <></>,
                            handleClick: () => {

                                setActiveCategory('')
                                dispatch(getParams(initialState))
                                // history.push('/analytics/portfolio-analytics')
                                window.open('/analytics/portfolio-analytics', '_blank');
                            },
                            route: ["/analytics/portfolio-analytics"],
                            isDisabled: false
                        }] : []),

                        // REVENUE ANALYTICS
                        ...(isModuleAllowed({ value: "AnalyticsDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Revenue Analytics',
                            activeIcon: <></>,
                            inActiveIcon: <></>,
                            handleClick: () => {
                                dispatch(getParams(initialState))
                                history.push('/analytics/revenue-analytics')
                            },
                            route: ["/analytics/revenue-analytics"],
                            isDisabled: true
                        }] : [])
                    ],
                },

                //   NON MF ORDERS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'ORDERS',
                        handleClick: () => setActiveCategory(activeCategory === 'orders' ? '' : 'orders'),
                        dropdownId: 'orders',
                        isDisabled: false
                    },
                    subMenuItem: [

                        // NON MF ORDER DASHBOARD
                        ...((ENABLE_ORDER_MANAGEMENT && isModuleAllowed({ value: "OrderDashboard", compareArr: permissions, objKey: "permissonCode" })) ? [{
                            name: 'Order Dashboard',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                history.push('/orders/order-dashboard')
                            },
                            route: ['/orders/order-dashboard'],
                            isDisabled: false
                        }] : []),

                        // NON MF ORDER ENTRY
                        ...((ENABLE_ORDER_MANAGEMENT && isModuleAllowed({ value: "OrderEntry", compareArr: permissions, objKey: "permissonCode" })) ? [{
                            name: 'Order Entry',
                            activeIcon: <SidebarDashboardIcon />,
                            inActiveIcon: <DashboardInactiveIcon />,
                            handleClick: () => {
                                history.push('/orders/order-entry')
                            },
                            route: ["/orders/order-entry"],
                            isDisabled: false
                        }] : []),

                        // ORDER APPROVALS
                        ...((ENABLE_ORDER_MANAGEMENT && isModuleAllowed({ value: "POAOrderApproval", compareArr: permissions, objKey: "permissonCode" })) ? [{
                            name: 'Order Approvals',
                            activeIcon: <SidebarDashboardIcon />,
                            inActiveIcon: <DashboardInactiveIcon />,
                            handleClick: () => {
                                history.push('/orders-approval')
                            },
                            route: ['/orders-approval'],
                            isDisabled: false
                        }] : [])
                    ],
                },

                // INVESTOR REPORTS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'OTHERS',
                        handleClick: () => setActiveCategory(activeCategory === 'others' ? '' : 'others'),
                        dropdownId: 'others',
                        isDisabled: false
                    },
                    subMenuItem: [

                        // INVESTOR REPORTS
                        ...((
                            [USER_ROLES.INVESTOR].includes(role) &&
                            isModuleAllowed({ value: "INVReports", compareArr: permissions, objKey: "permissonCode" })
                        ) ? [{
                            name: 'Reports',
                            activeIcon: <InvestorActiveReportsIcon />,
                            inActiveIcon: <InvestorReportsIcon />,
                            handleClick: () => {
                                history.push('/investor-reports/client-statement')
                            },
                            route: [
                                "/investor-reports/client-statement",
                                "/investor-reports/mf-transaction-wise-holding-report",
                                "/investor-reports/holding-report",
                                "/investor-reports/transaction-history-report",
                                "/investor-reports/capital-gain-statement",
                                // "/investor-reports/ria-report",
                                "/investor-reports/corporate-action-report"
                            ],
                            isDisabled: false
                        }] : []),
                    ],
                },

                // CHECKER PAYMENT APPROVALS
                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'PAYMENTS',
                        handleClick: () => setActiveCategory(activeCategory === 'payments' ? '' : 'payments'),
                        dropdownId: 'payments',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // PAYMENT APPROVALS
                        ...((ENABLE_PAYMENT_CHECKER && isModuleAllowed({ value: "POAPaymentApproval", compareArr: permissions, objKey: "permissonCode" })) ? [{
                            name: 'Payment Approvals',
                            activeIcon: <PaymentsActiveIcon />,
                            inActiveIcon: <PaymentsInactiveIcon />,
                            handleClick: () => {
                                history.push('/payments-approval')
                            },
                            route: ['/payments-approval'],
                            isDisabled: false
                        }] : []),
                    ],
                },

                // SETTLEMENTS
                ...((isModuleAllowed({ value: "settlementApproval", compareArr: permissions, objKey: "permissonCode" })) ? [{
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'SETTLEMENTS',
                        handleClick: () => setActiveCategory(activeCategory === 'settlements' ? '' : 'settlements'),
                        dropdownId: 'settlements',
                        isDisabled: false
                    },
                    subMenuItem: [
                        // SETTLEMENT APPROVALS
                        {
                            name: 'Settlement Approvals',
                            activeIcon: <PaymentsActiveIcon />,
                            inActiveIcon: <PaymentsInactiveIcon />,
                            handleClick: () => {
                                history.push('/settlements-approval')
                            },
                            route: ['/settlements-approval'],
                            isDisabled: false
                        },
                    ],
                }] : []),

                // POST ONBOARDING BANK

                // {
                //     isSubMenuItem: true,
                //     menuItem: {
                //         name: 'BANKS',
                //         handleClick: () => setActiveCategory(activeCategory === 'banks' ? '' : 'banks'),
                //         dropdownId: 'banks',
                //         isDisabled: false
                //     },
                //     subMenuItem: [
                //         ...((
                //             isModuleAllowed({ value: "MFOrders", compareArr: permissions, objKey: "permissonCode" }) ||
                //             isModuleAllowed({ value: "OrderRecommend", compareArr: permissions, objKey: "permissonCode" })
                //         ) ? [{
                //             name: 'Add/Update Bank',
                //             activeIcon: <ActiveAddNewIcon />,
                //             inActiveIcon: <AddNewIcon />,
                //             handleClick: () => {
                //                 history.push('/bank/updateBank')
                //             },
                //             route: ['/bank/updateBank'],
                //             isDisabled: false
                //         }] : []),
                //         ...((
                //             isModuleAllowed({ value: "MFOrders", compareArr: permissions, objKey: "permissonCode" }) ||
                //             isModuleAllowed({ value: "OrderRecommend", compareArr: permissions, objKey: "permissonCode" })
                //         ) ? [{
                //             name: 'View Bank Status',
                //             activeIcon: <ActiveViewExistingIcon />,
                //             inActiveIcon: <ViewExistingIcon />,
                //             handleClick: () => {
                //                 history.push('/bank/viewBank')
                //             },
                //             route: ['/bank/viewBank'],
                //             isDisabled: false
                //         }] : [])
                //     ],
                // },

                // POST ONBOARDING NOMINEE

                // {
                //     isSubMenuItem: true,
                //     menuItem: {
                //         name: 'NOMINEE',
                //         handleClick: () => setActiveCategory(activeCategory === 'nominee' ? '' : 'nominee'),
                //         dropdownId: 'nominee',
                //         isDisabled: false
                //     },
                //     subMenuItem: [
                //         ...((
                //             isModuleAllowed({ value: "MFOrders", compareArr: permissions, objKey: "permissonCode" }) ||
                //             isModuleAllowed({ value: "OrderRecommend", compareArr: permissions, objKey: "permissonCode" })
                //         ) ? [{
                //             name: 'Add/Update Details',
                //             activeIcon: <ActiveAddNewIcon />,
                //             inActiveIcon: <AddNewIcon />,
                //             handleClick: () => {
                //                 history.push('/nominee/updateNominee')
                //             },
                //             route: ['/nominee/updateNominee'],
                //             isDisabled: false
                //         }] : [])
                //     ],
                // },

                // RIA FEE CALCULATION

                {
                    isSubMenuItem: true,
                    menuItem: {
                        name: 'RIA FEE',
                        handleClick: () => setActiveCategory(activeCategory === 'riaFee' ? '' : 'riaFee'),
                        dropdownId: 'riaFee',
                        isDisabled: false
                    },
                    subMenuItem: [
                        ...(isModuleAllowed({ value: "riaReport", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'RIA Report',
                            activeIcon: <ActiveViewEmailConsentIcon />,
                            inActiveIcon: <ViewEmailConsentIcon />,
                            handleClick: () => {
                                history.push('/ria-report')
                            },
                            route: ['/ria-report'],
                            isDisabled: false
                        }] : []),
                        ...(isModuleAllowed({ value: "riaControlDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'RIA Dashboard',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                history.push('/ria-dashboard')
                            },
                            route: ['/ria-dashboard'],
                            isDisabled: false
                        }] : []),
                        ...(isModuleAllowed({ value: "riaFeeSetUp", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'RIA Fee Setup',
                            activeIcon: <ActiveAddNewIcon />,
                            inActiveIcon: <AddNewIcon />,
                            handleClick: () => {
                                history.push('/ria-fee-setup')
                            },
                            route: [
                                '/ria-fee-setup',
                                '/ria-fee-setup/:familyId/modify',
                                '/ria-fee-setup/history'
                            ],
                            isDisabled: false
                        }] : []),
                        ...(isModuleAllowed({ value: "riaSummary", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'RIA Summary',
                            activeIcon: <ActiveViewExistingIcon />,
                            inActiveIcon: <ViewExistingIcon />,
                            handleClick: () => {
                                history.push('/ria-summary')
                            },
                            route: ['/ria-summary'],
                            isDisabled: false
                        }] : []),
                        ...(isModuleAllowed({ value: "riaSecurityExclusionManagement", compareArr: permissions, objKey: "permissonCode" }) ? [{
                            name: 'Exclusion Mgmt',
                            activeIcon: <ActiveAddNewIcon />,
                            inActiveIcon: <AddNewIcon />,
                            handleClick: () => {
                                history.push('/security-exclusion-management')
                            },
                            route: [
                                '/security-exclusion-management',
                                '/security-exclusion-management/audit-history'
                            ],
                            isDisabled: false
                        }] : [])
                    ],
                },

                // USER MANAGEMENT
                ...(isModuleAllowed({ value: "RML", compareArr: permissions, objKey: "permissonCode" }) ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'User Management',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/user-management'),
                        route: ['/user-management/relationship-managers'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // ADMIN SUPPORT
                ...(USER_ROLES.AMC_ADMIN === role ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'Admin Support',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/admin-support'),
                        route: ['/admin-support'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),

                // AMC ADMINS
                ...(USER_ROLES.SUPER_ADMIN === role ? [{
                    isSubMenuItem: false,
                    menuItem: {
                        name: 'AMC Admins',
                        activeIcon: <></>,
                        inActiveIcon: <></>,
                        handleClick: () => history.push('/amc-admins'),
                        route: ['/amc-admins'],
                        isDisabled: false
                    },
                    subMenuItem: [],
                }] : []),
            ]
        )
    }, [activeCategory, setActiveCategory])

    return sideBarData
}